import html2canvas from 'html2canvas';

// ─── Name on share cards ──────────────────────────────────────────────────

export const ANON_SHARE_NAME = 'Lightway Advocate';
export const SHARE_NAME_MODES = ['account', 'first', 'custom', 'hidden'];
export const SHARE_CUSTOM_NAME_MAX = 40;

/** The user's chosen name mode; older prefs only have the shareAnonymous boolean. */
export const getShareNameMode = (prefs) => {
  if (prefs && SHARE_NAME_MODES.includes(prefs.shareNameMode)) return prefs.shareNameMode;
  return prefs?.shareAnonymous ? 'hidden' : 'account';
};

/**
 * Name shown on share cards and public share pages.
 * Under-18 rule: anyone not confirmed 18+ is always "Lightway Advocate".
 */
export const resolveShareName = (prefs, accountName = '') => {
  if (!prefs || prefs.isAdultConfirmed !== true) return ANON_SHARE_NAME;
  const full = (accountName || '').trim();
  switch (getShareNameMode(prefs)) {
    case 'account':
      return full || ANON_SHARE_NAME;
    case 'first':
      return full.split(/\s+/)[0] || ANON_SHARE_NAME;
    case 'custom':
      return (prefs.shareCustomName || '').trim().slice(0, SHARE_CUSTOM_NAME_MAX) || ANON_SHARE_NAME;
    default:
      return ANON_SHARE_NAME;
  }
};

/**
 * Generate shareable text with hashtags
 * @param {Object} activity - Activity object
 * @returns {string} - Formatted share text
 */
export const generateShareText = (activity) => {
  const activityTitle = activity.title || 'an advocacy activity';
  const causeName = activity.cause?.title || 'my cause';

  let text = `Just completed ${activityTitle} for ${causeName}! 🙌`;

  // Full reflection (the card image only has room for a short excerpt)
  const reflection = (activity.reflection || '').trim();
  if (reflection) {
    text += `\n\n${reflection}`;
  }

  // Add public document links if present
  if (activity.documentLinks && activity.documentLinks.trim()) {
    const links = activity.documentLinks.trim().split('\n').filter(l => l.trim());
    if (links.length > 0) {
      text += '\n';
      links.forEach(link => {
        text += `\n📄 ${link.trim()}`;
      });
    }
  }

  // Add video link if present
  if (activity.videoUrl) {
    text += `\n\n🎬 Watch: ${activity.videoUrl}`;
  }

  const hashtags = generateHashtags(activity);

  return `${text}\n\n${hashtags}`;
};

/**
 * Generate hashtags for an activity
 * @param {Object} activity - Activity object
 * @returns {string} - Space-separated hashtags
 */
export const generateHashtags = (activity) => {
  const hashtags = [];
  
  // Level hashtag (if available)
  if (activity.cause?.level?.title) {
    const levelHashtag = activity.cause.level.title
      .replace(/\s+/g, '')
      .replace(/[^a-zA-Z0-9]/g, '');
    hashtags.push(`#${levelHashtag}`);
  }
  
  // Cause hashtag
  if (activity.cause?.title) {
    const causeHashtag = activity.cause.title
      .replace(/\s+/g, '')
      .replace(/[^a-zA-Z0-9]/g, '');
    hashtags.push(`#${causeHashtag}`);
  }
  
  // App hashtags
  hashtags.push('#LightwayApp');
  hashtags.push('#AdvocacyInAction');
  
  return hashtags.join(' ');
};

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} - Success status
 */
export const copyToClipboard = async (text) => {
  try {
    // Modern Clipboard API
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    
    // Fallback for older browsers or non-secure contexts
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    return successful;
  } catch (error) {
    console.error('Failed to copy text:', error);
    return false;
  }
};

/**
 * Copy text that is still being produced (e.g. a caption waiting on a share
 * link). Safari only allows clipboard writes during the tap, so the write is
 * started synchronously with a promised Blob; other browsers fall back to
 * writeText once the text resolves.
 * @param {Promise<string>} textPromise
 * @returns {Promise<boolean>}
 */
export const copyTextDeferred = async (textPromise) => {
  try {
    if (navigator.clipboard?.write && window.ClipboardItem && window.isSecureContext) {
      const item = new window.ClipboardItem({
        'text/plain': textPromise.then(t => new Blob([t], { type: 'text/plain' })),
      });
      await navigator.clipboard.write([item]);
      return true;
    }
  } catch {
    // fall through to writeText
  }
  try {
    return await copyToClipboard(await textPromise);
  } catch {
    return false;
  }
};

/**
 * Pre-render the activity image into a CORS-safe data URL for html2canvas.
 *
 * The image is rendered at its natural aspect ratio, scaled to fit within
 * (maxWidth x maxHeight) — contain semantics. This preserves the full image
 * for any aspect ratio: tall posters, square photos, and wide panoramas all
 * appear without cropping. The card sizes the <img> to the data URL's
 * natural dimensions so the visible slot grows/shrinks to match.
 *
 * @param {string} imageUrl - Source image URL
 * @param {Object} [opts]
 * @param {number} [opts.maxWidth=552]
 * @param {number} [opts.maxHeight=480]
 * @param {number} [opts.pixelRatio=1] - bitmap density (2 for sharp 2x exports)
 * @returns {Promise<{dataUrl: string, width: number, height: number} | null>}
 */
export const prepareShareImage = async (imageUrl, opts = {}) => {
  if (!imageUrl) return null;

  const { maxWidth = 552, maxHeight = 480, pixelRatio = 1 } = opts;

  const renderToDataUrl = (img) => {
    const aspect = img.naturalWidth / img.naturalHeight;

    // contain: scale to fit within (maxWidth, maxHeight) preserving aspect
    let width = maxWidth;
    let height = maxWidth / aspect;
    if (height > maxHeight) {
      height = maxHeight;
      width = maxHeight * aspect;
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * pixelRatio);
    canvas.height = Math.round(height * pixelRatio);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    // width/height are CSS pixels; the bitmap is pixelRatio times larger.
    return {
      dataUrl: canvas.toDataURL('image/jpeg', 0.92),
      width: Math.round(width),
      height: Math.round(height),
    };
  };

  // Primary: fetch as blob → object URL → draw to canvas (avoids CORS canvas taint)
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    const result = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          resolve(renderToDataUrl(img));
        } catch (e) {
          reject(e);
        } finally {
          URL.revokeObjectURL(objectUrl);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('blob image load failed'));
      };
      img.src = objectUrl;
    });

    return result;
  } catch {
    // fetch failed (CORS or network) — fall back to Image + crossOrigin
  }

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        resolve(renderToDataUrl(img));
      } catch {
        resolve(null); // canvas tainted — caller falls back to original URL
      }
    };
    img.onerror = () => resolve(null);
    img.src = imageUrl;
  });
};

/**
 * Generate share image from ShareCard component
 * @param {HTMLElement} cardElement - The DOM element to capture
 * @returns {Promise<string>} - Base64 data URL of the image
 */
export const generateShareImage = async (cardElement, { scale = 2, backgroundColor = '#FFFFFF' } = {}) => {
  try {
    // Wait for all images in the card to fully load before capturing
    const images = cardElement.querySelectorAll('img');
    await Promise.all(
      Array.from(images).map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise(resolve => {
          img.onload = resolve;
          img.onerror = resolve; // Resolve even on error so we don't hang
        });
      })
    );

    const canvas = await html2canvas(cardElement, {
      backgroundColor,
      scale,
      logging: false,
      useCORS: true,
      allowTaint: false,
      imageTimeout: 15000,
    });

    return canvas.toDataURL('image/png');
  } catch (error) {
    console.error('Failed to generate share image:', error);
    throw error;
  }
};

/**
 * Download image as PNG file
 * @param {string} dataUrl - Base64 data URL of the image
 * @param {string} filename - Desired filename (without extension)
 */
export const downloadImage = (dataUrl, filename = 'lightway-activity') => {
  const link = document.createElement('a');
  link.download = `${filename}.png`;
  link.href = dataUrl;
  link.click();
};

/**
 * Convert base64 data URL to Blob
 * @param {string} dataUrl - Base64 data URL
 * @returns {Blob} - Image blob
 */
export const dataURLtoBlob = (dataUrl) => {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  
  return new Blob([u8arr], { type: mime });
};

/**
 * Get platform-specific screenshot instructions
 * @returns {Object} - Instructions for different platforms
 */
export const getScreenshotInstructions = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Detect iOS
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return {
      platform: 'iOS',
      instruction: 'Press the Power button and Volume Up button at the same time',
      icon: '📱'
    };
  }
  
  // Detect Android
  if (/android/i.test(userAgent)) {
    return {
      platform: 'Android',
      instruction: 'Press and hold the Power button and Volume Down button together',
      icon: '📱'
    };
  }
  
  // Desktop (Windows/Mac/Linux)
  if (/Mac/i.test(userAgent)) {
    return {
      platform: 'Mac',
      instruction: 'Press Command + Shift + 4, then click and drag to select the area',
      icon: '💻'
    };
  }
  
  return {
    platform: 'Windows',
    instruction: 'Press Windows Key + Shift + S to open Snip & Sketch',
    icon: '💻'
  };
};

/**
 * Format activity data for sharing
 * @param {Object} activity - Activity object
 * @returns {Object} - Formatted activity data
 */
export const formatActivityForSharing = (activity) => {
  return {
    title: activity.title,
    cause: activity.cause?.title,
    level: activity.cause?.level?.title,
    reflection: activity.reflection,
    imageUrl: activity.imageUrl || activity.thumbnailUrl,
    timestamp: activity.timestamp,
  };
};
